import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  Box,
  LinearProgress,
  Button,
} from '@mui/material';
import { ArrowCircleLeftOutlined, ArrowCircleRightOutlined, Autorenew } from '@mui/icons-material';
import {
  ISectionStatistics,
  ISiddurTopSectionStatistics,
  ISiddurSectionStatistics,
} from '@shared/types';
import SectionStatistics from '../core/SectionStatistics';
import { LoaderComponent } from '../core/LoaderComponent';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { numberToGematria } from '../../../utils/text-hebrew.utils';

interface SiddurAccordionProps {
  bookName: string;
  levelOneData: ISiddurTopSectionStatistics;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  isTeacher: boolean;
  isOnlyAccordion?: boolean;
}

const SiddurAccordion: React.FC<SiddurAccordionProps> = ({
  bookName,
  levelOneData,
  expanded,
  onChange,
  isTeacher,
}) => {
  const booksStore = useStoreByName('booksStore');

  // State management for Siddur structure
  const [selectedLevelTwo, setSelectedLevelTwo] = useState<ISiddurSectionStatistics | ISectionStatistics | null>(null);

  const [selectedLevelThree, setSelectedLevelThree] = useState<ISectionStatistics | null>(null);

  // Loading state for handleReadAgain
  const [isLoadingReadAgain, setIsLoadingReadAgain] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Reset selections when switching books
  useEffect(() => {
    const siddurData = levelOneData;
    
    // בדיקה אם יש section_array (4-5 רמות)
    if (siddurData?.section_array && siddurData.section_array.length > 0) {
      // 4-5 רמות - יש section_array
      setSelectedLevelTwo(siddurData.section_array[0]);
      
      // Check if there are sub-sections
      const firstSection = siddurData.section_array[0];
      if (firstSection?.sub_section_array && firstSection.sub_section_array.length > 0) {
        // Has sub-sections - use them
        setSelectedLevelThree(firstSection.sub_section_array[0]);
      } else if (firstSection) {
        // No sub-sections - statistics are directly on the section
        setSelectedLevelThree({
          section_name: firstSection.section_name_he || '',
          section_name_he: firstSection.section_name_he || '',
          by_heart: firstSection.by_heart || { read_count: 0, last_score: 0, average_score: 0, high_score: 0 },
          in_writing: firstSection.in_writing || { read_count: 0, last_score: 0, average_score: 0, high_score: 0 },
          hierarchy_info: firstSection.hierarchy_info || {
            category_name: siddurData?.sub_category_name_he || '',
            sub_category_name: siddurData?.top_section_name_he || '',
            top_section_name: firstSection.section_name_he || '',
            section_name: firstSection.section_name_he || '',
          }
        });
      } else {
        setSelectedLevelThree(null);
      }
    } else {
      setSelectedLevelTwo(null);
      setSelectedLevelThree(null);
    }
  }, [levelOneData]);

  // Update level three when level two changes
  useEffect(() => {
    const siddurSection = selectedLevelTwo;
    
    if (!siddurSection) {
      setSelectedLevelThree(null);
      return;
    }
    
    if ('sub_section_array' in siddurSection && siddurSection.sub_section_array && siddurSection.sub_section_array.length > 0) {
      // Has sub-sections - use them
      setSelectedLevelThree(siddurSection.sub_section_array[0]);
    } else if ('by_heart' in siddurSection && 'in_writing' in siddurSection) {
      // No sub-sections - statistics are directly on the section
      // בדיקה שהאובייקט הוא ISectionStatistics
      if ('section_name_he' in siddurSection) {
        setSelectedLevelThree(siddurSection as ISectionStatistics);
      } else {
        // אם זה ISiddurSectionStatistics, נצטרך ליצור ISectionStatistics עם hierarchy_info
        const siddurSectionTyped = siddurSection as ISiddurSectionStatistics;
        setSelectedLevelThree({
          section_name: siddurSectionTyped.section_name_he || '',
          section_name_he: siddurSectionTyped.section_name_he || '',
          by_heart: siddurSectionTyped.by_heart || { read_count: 0, last_score: 0, average_score: 0, high_score: 0 },
          in_writing: siddurSectionTyped.in_writing || { read_count: 0, last_score: 0, average_score: 0, high_score: 0 },
          hierarchy_info: siddurSectionTyped.hierarchy_info || {
            category_name: levelOneData?.sub_category_name_he || '', // שער (למשל "ימי חול")
            sub_category_name: levelOneData?.top_section_name_he || '', // חלק (למשל "תפילת שחרית") 
            top_section_name: siddurSectionTyped.section_name_he || '', // נושא (למשל "עמידה")
            section_name: siddurSectionTyped.section_name_he || '', // תפילה/ברכה (למשל "קדושת השם")
          }
        });
      }
    } else {
      setSelectedLevelThree(null);
    }
  }, [selectedLevelTwo, levelOneData]);

  const handleReadAgain = async () => {
    setIsLoadingReadAgain(true);
    
    // Start timer for minimum loading time
    const startTime = Date.now();
    
    try {

      // בדיקה אם יש מידע היררכי
      if (!selectedLevelThree?.hierarchy_info) {
        console.error('❌ Siddur - Missing hierarchy_info in selectedLevelThree');
        setIsLoadingReadAgain(false);
        return;
      }

      const hierarchyInfo = selectedLevelThree.hierarchy_info;


      const result = await booksStore.setReadingContext({
        bookName: 'Siddur_Ashkenaz', // שם קבוע לסידור עם קו תחתון
        levelOneLabel: hierarchyInfo.category_name || '', // שער (למשל "ימי חול")
        levelTwoLabel: hierarchyInfo.sub_category_name || '', // חלק (למשל "תפילת שחרית")
        levelThreeLabel: hierarchyInfo.top_section_name?.toString() || '', // נושא (למשל "עמידה")
        levelFourLabel: hierarchyInfo.section_name?.toString() || '', // תפילה/ברכה (למשל "קדושת השם")
      });

      
      if (!result) {
        setIsLoadingReadAgain(false);
        return;
      }

      // Ensure minimum loading time of 300ms
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsedTime));
      }

      // Determine if we're in student area or teacher's personal area
      const isStudentArea = location.pathname === '/student-area';

      if (isStudentArea) {
        // Navigate with state to indicate we came from student area
        navigate('/reading', {
          state: {
            fromStudentArea: true,
            studentData: location.state?.studentData,
            studentName: location.state?.studentName,
            studentId: location.state?.studentId,
          },
        });
      } else {
        // Navigate with state to indicate we came from personal area
        navigate('/reading', { state: { fromPersonalArea: true } });
      }
    } catch (error) {
      console.error('❌ Siddur - Error in handleReadAgain:', error);
      setIsLoadingReadAgain(false);
    }
  };

  // Show loader when loading
  if (isLoadingReadAgain) {
    return <LoaderComponent />;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      slotProps={{ transition: { timeout: { enter: 600, exit: 800 } } }}
    >
      <AccordionSummary
        expandIcon={i18n.language === 'en' ? <ArrowCircleRightOutlined /> : <ArrowCircleLeftOutlined />}
      >
        <div className="headers">
          <h3 style={{ color: 'primary' }}>
            {levelOneData?.top_section_name_he || 'סידור אשכנז'}
          </h3>
          <h5>
            {(() => {
              if (levelOneData?.section_array && levelOneData.section_array.length > 0) {
                // הצג את הטופ סקשן ואת תת-הקטגוריה
                return `${levelOneData?.sub_category_name_he || ''}`;
              }
              return '';
            })()}
          </h5>
        </div>
        <div className="progress">
          <label>
            {`${t('manyReadingSections')}: ${
              (() => {
                if (levelOneData?.section_array) {
                  // 4-5 רמות - יש section_array
                  let totalWithScores = 0;
                  
                  levelOneData.section_array.forEach((section: ISiddurSectionStatistics) => {
                    if (section.sub_section_array && section.sub_section_array.length > 0) {
                      // יש תת-סעיפים - ספור כמה יש ציונים
                      section.sub_section_array.forEach((subSection: ISectionStatistics) => {
                        if (subSection.by_heart.read_count > 0 || subSection.in_writing.read_count > 0) {
                          totalWithScores++;
                        }
                      });
                    } else {
                      // אין תת-סעיפים - בדוק אם יש ציונים לסקשן עצמו
                      if (section.by_heart && (section.by_heart.read_count > 0 || (section.in_writing && section.in_writing.read_count > 0))) {
                        totalWithScores++;
                      }
                    }
                  });
                  
                  return totalWithScores;
                }
                return 0;
              })()
            }`}
          </label>
          <div style={{ transform: 'scaleX(-1)' }}>
            <LinearProgress 
              variant="determinate" 
              value={
                (() => {
                  if (levelOneData?.section_array) {
                    let totalWithScores = 0;
                    
                    levelOneData.section_array.forEach((section: ISiddurSectionStatistics) => {
                      if (section.sub_section_array && section.sub_section_array.length > 0) {
                        // יש תת-סעיפים
                        section.sub_section_array.forEach((subSection: ISectionStatistics) => {
                          if (subSection.by_heart.read_count > 0 || subSection.in_writing.read_count > 0) {
                            totalWithScores++;
                          }
                        });
                      } else {
                        // אין תת-סעיפים
                        if (section.by_heart && (section.by_heart.read_count > 0 || (section.in_writing && section.in_writing.read_count > 0))) {
                          totalWithScores++;
                        }
                      }
                    });
                    
                    // תמיד נשתמש ב-10 כ-100%
                    return Math.round((totalWithScores / 10) * 100);
                  }
                  return 0;
                })()
              } 
            />
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="container">
          {/* First column - Sections */}
          {(() => {
            if (levelOneData?.section_array && levelOneData.section_array.length > 0) {
              // 4-5 רמות - הצג את ה-section_array
              return (
                <div className="column">
                  <h5 style={{ paddingInline: '6px' }}>חלקים</h5>
                  <List>
                    {levelOneData.section_array.map((levelTwo, index) => (
                      <ListItemButton
                        key={levelTwo.section_id}
                        selected={selectedLevelTwo === levelTwo}
                        onClick={() => setSelectedLevelTwo(levelTwo)}
                      >
                        <ListItemText primary={<p>{numberToGematria(levelTwo.section_name_he ?? index + 1)}</p>} />
                      </ListItemButton>
                    ))}
                  </List>
                </div>
              );
            }
            return null;
          })()}

          {/* Second column - Sub Sections (רק עבור 4-5 רמות) */}
          {(() => {
            if (selectedLevelTwo && 'sub_section_array' in selectedLevelTwo && selectedLevelTwo.sub_section_array && selectedLevelTwo.sub_section_array.length > 0) {
              return (
                <div className="column">
                  <h5 style={{ paddingInline: '6px' }}>נושאים</h5>
                  <List>
                    {selectedLevelTwo.sub_section_array.map((levelThree, index) => (
                      <ListItemButton
                        key={`${levelThree.section_name_he}-${index}`}
                        selected={selectedLevelThree?.section_name === levelThree.section_name_he}
                        onClick={() => setSelectedLevelThree(levelThree)}
                      >
                        <ListItemText primary={<p>{numberToGematria(levelThree.section_name_he)}</p>} />
                      </ListItemButton>
                    ))}
                  </List>
                </div>
              );
            }
            return null;
          })()}

          {/* Third column - Statistics */}
          <div className="column">
            {selectedLevelThree && (
              <Box>
                <div className="details">
                  <div className="head-line">
                    <h3>
                      {(() => {
                        if (!selectedLevelThree) return '';
                        
                        // בדיקה אם זה תת-סעיף או סעיף רגיל
                        if ('sub_section_array' in selectedLevelThree) {
                          // זה סעיף רגיל - השתמש ב-section_name_he
                          return String(selectedLevelThree.section_name_he || '');
                        } else {
                          // זה תת-סעיף - השתמש ב-section_name_he
                          return String(selectedLevelThree.section_name_he || '');
                        }
                      })()}
                    </h3>
                    <h4>
                      {(() => {
                        // בדיקה אם יש תת-סעיפים
                        if (selectedLevelTwo && 'sub_section_array' in selectedLevelTwo && selectedLevelTwo.sub_section_array && selectedLevelTwo.sub_section_array.length > 0) {
                          // יש תת-סעיפים - הצג: Section | Top_Section
                          return `${selectedLevelTwo?.section_name_he || ''} | ${levelOneData?.top_section_name_he || ''}`;
                        } else {
                          // אין תת-סעיפים - הצג: Top_Section | Sub_Category
                          return `${levelOneData?.top_section_name_he || ''} | ${levelOneData?.sub_category_name_he || ''}`;
                        }
                      })()}
                    </h4>
                  </div>
                  <SectionStatistics
                    readingStats={selectedLevelThree.in_writing}
                    byHeartStats={selectedLevelThree.by_heart}
                    showAverageScore={true}
                  />
                </div>
                {!isTeacher && (
                  <Button 
                    className="circle" 
                    onClick={handleReadAgain} 
                    startIcon={<Autorenew />}
                    disabled={isLoadingReadAgain}
                  >
                    {t('readAgain')}
                  </Button>
                )}
              </Box>
            )}
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default observer(SiddurAccordion);
